/**
 * Методология расчёта этапов 2–4 (2026 / 2027).
 *
 * Значения по обеим методологиям рассчитывает БД; сервер отдаёт данные этапов
 * уже по активной методологии под прежними ключами. Клиент только показывает
 * переключатель и перечитывает данные этапов после смены.
 *
 * Не путать с «Методологией» — вики-разделом (services/methodology).
 */
import { api } from '../core/api';

export const CALC_METHODOLOGIES = [2026, 2027] as const;
export type CalcMethodology = (typeof CALC_METHODOLOGIES)[number];

/**
 * RBAC-ресурс без parent: «Запись»/«Полный доступ» — переключение, «Чтение» — журнал.
 * Выдаётся ролям в админке (по умолчанию — только администраторам).
 */
export const CALC_METHODOLOGY_RESOURCE = 'sales_calc_methodology';

/** Методология выбирается для года планирования: для 2026 — только 2026, с 2027 — выбор. */
export interface CalcMethodologyState {
  year: number;
  methodology: CalcMethodology;
  available: CalcMethodology[];
  defaultMethodology: CalcMethodology;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CalcMethodologyChange {
  year: number;
  previous: CalcMethodology;
  current: CalcMethodology;
  changed: boolean;
  changedAt: string;
}

export interface CalcMethodologyLogEntry {
  id: number;
  year: number;
  previousMethodology: CalcMethodology | null;
  newMethodology: CalcMethodology;
  changedByUserId: string | null;
  changedByName: string | null;
  changedByEmail: string | null;
  impersonatedBy: string | null;
  changedAt: string;
}

/** Поля утверждённой версии (снапшота) — что отдаёт сервер для 3s1s / 4s2s. */
export interface CalcMethodologySnapshotInfo {
  /** Методология, по которой сохранены значения версии. */
  methodology?: CalcMethodology;
  /** Версия создана до появления переключателя — считается методологией 2026. */
  methodologyLegacy?: boolean;
}

/** Подпись методологии версии для списков и строк метаданных. */
export function snapshotMethodologyLabel({ methodology, methodologyLegacy }: CalcMethodologySnapshotInfo): string {
  if (!methodology) return '';
  return methodologyLegacy ? `методология ${methodology} (до переключателя)` : `методология ${methodology}`;
}

/**
 * Описание для «i» рядом с переключателем. Информационный текст (из описаний
 * колонок мет_* в БД) — по нему приложение ничего не считает.
 */
export const CALC_METHODOLOGY_DESCRIPTIONS: Record<CalcMethodology, { title: string; priceOrder: string[] }> = {
  2026: {
    title: 'Методология 2026',
    priceOrder: [
      'Ручная цена',
      'Цена по компании, если были продажи',
      'Регистрационная цена (есть только у ЖНВЛП)',
      'Цена по рынку',
    ],
  },
  2027: {
    title: 'Методология 2027',
    priceOrder: [
      'Ручная цена',
      'Регистрационная цена (есть только у ЖНВЛП)',
      'Цена по компании, если были продажи',
      'Цена по рынку',
    ],
  },
};

/** Корни ключей React Query с данными этапов 2–4 и сверки — зависят от методологии. */
export const CALC_METHODOLOGY_DEPENDENT_QUERY_ROOTS: readonly string[] = [
  'factPlanTnCpr',
  'bgCprTnPeriods',
  'stage3BgTnCprSkuPlanSales',
  'stage4MpDistribution',
  'dynPlanCprTnSku',
];

/**
 * Утверждение: от методологии зависит только проверка (`[root, 'status', …]`).
 * Версии (снапшоты) хранят сохранённые значения и не перечитываются.
 */
const CALC_METHODOLOGY_DEPENDENT_APPROVAL_ROOTS: readonly string[] = ['acceptStage3Plan', 'acceptPlanStage4Mp'];

export function isCalcMethodologyDependentQuery(queryKey: readonly unknown[]): boolean {
  const root = String(queryKey[0]);
  return CALC_METHODOLOGY_DEPENDENT_QUERY_ROOTS.includes(root)
    || (CALC_METHODOLOGY_DEPENDENT_APPROVAL_ROOTS.includes(root) && queryKey[1] === 'status');
}

export const calcMethodologyService = {
  getState: (year: number) => api.get<CalcMethodologyState>(`/calc-methodology?year=${year}`),

  setMethodology: (year: number, methodology: CalcMethodology) =>
    api.put<CalcMethodologyChange>('/calc-methodology', { year, methodology }),

  getLog: (year: number, limit = 50) =>
    api.get<CalcMethodologyLogEntry[]>(`/calc-methodology/log?year=${year}&limit=${limit}`),
};
